import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { AutocompleteRenderInputParams, FormInputText, PaginatedSearchDropdown } from '@hopskipdrive/react-components';

import { CalendarOption, useCalendarOptions } from '../../../hooks/useCalendarOptions';

interface CalendarDropdownProps {
  initialValue?: CalendarOption;
  onChange: (value: string) => void;
  disabled?: boolean;
  value?: string | null;
  error?: boolean;
  helperText?: ReactNode;
}

export const CalendarDropdown: FC<CalendarDropdownProps> = ({
  initialValue,
  onChange,
  disabled,
  value,
  error,
  helperText,
}) => {
  const { calendarOptions, queryParams, handlePagination, debounceHandleQuery, isLoading, addMissingCalendar } =
    useCalendarOptions(initialValue);
  const [calendarInputValue, setCalendarInputValue] = useState<string>('');
  const [currentValue, setCurrentValue] = useState<string>(value || '');

  useEffect(() => {
    if (value && typeof value === 'string') {
      addMissingCalendar(value);

      if (currentValue !== value && calendarInputValue) {
        setCalendarInputValue('');
        setCurrentValue(value);
      }
    }
  }, [value]);

  const selectedValue = useMemo(
    () => calendarInputValue || calendarOptions.find((option) => option.value === value)?.label,
    [calendarInputValue, calendarOptions, value]
  );

  return (
    <PaginatedSearchDropdown
      id="calendarSlug"
      data-testid="calendars-dropdown"
      options={calendarOptions}
      disabled={disabled}
      inputValue={calendarInputValue}
      setInputValue={setCalendarInputValue}
      perPage={queryParams.perPage}
      pageNumber={queryParams.page}
      isLoading={isLoading}
      {...(selectedValue && { value: selectedValue })}
      onChange={(_, value) => {
        debounceHandleQuery('');
        onChange(value?.value);
        if (!value || typeof value === 'string') {
          return;
        }
        setCurrentValue(value.value);
        setCalendarInputValue(value.label);
      }}
      loadNextPage={handlePagination}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <FormInputText
          {...params}
          variant="outlined"
          fullWidth
          placeholder="Select a Calendar"
          onChange={(e) => {
            debounceHandleQuery(e.target.value);
          }}
          error={error}
          helperText={helperText}
        />
      )}
    />
  );
};
